import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MonacoEditorService } from '@iupics-components/standard/monaco-editor/monaco-editor.service';
import { DataUtils } from '@iupics-util/tools/data.utils';
import * as _monaco from 'monaco-editor';
import { wireTmGrammars } from 'monaco-editor-textmate';
import { Registry } from 'monaco-textmate';
import { filter, interval, Observable, ReplaySubject, take } from 'rxjs';
import { drools } from './registry/drools.registry';
import { getRegistor, RegistorLanguage } from './registry/registry';

@Component({
  templateUrl: './monaco-editor.component.html',
  styleUrls: ['./monaco-editor.component.scss'],
  selector: 'iu-monaco-editor',
  imports: [CommonModule],
  standalone: true,
  providers: [MonacoEditorService],
})
export class MonacoEditorComponent implements OnInit, OnDestroy {
  private readonly uuid = DataUtils.uuid();

  widthOptions = input<WidthOptions>();
  heightOptions = input<HeightOptions>();
  language = input<string>('text');
  readonly = input<boolean>(false);

  readonly editorReady = output<EditorReadyEvent>();
  readonly editorDestroy = output<EditorDestroyEvent>();
  readonly editorSave = output<EditorSaveEvent>();
  readonly editorContentChange = output<EditorContentChangeEvent>();

  getWidthOptions = computed(() => ({ ...defaultValueWidthOptions, ...this.widthOptions() }));
  getHeightOptions = computed(() => ({ ...defaultValueHeightOptions, ...this.heightOptions() }));

  private editor?: _monaco.editor.IStandaloneCodeEditor; // editor instance to e.g. read value by this.editor.getValue()

  private readonly destroyRef = inject(DestroyRef);
  private readonly editorService = inject(MonacoEditorService);

  @HostListener('window:resize')
  onWindowResize(): void {
    this.resetSize();
  }

  @HostListener('keydown.control.s', ['$event'])
  onSave(event: KeyboardEvent): void {
    event.preventDefault();
    const value = this.editor?.getValue();
    this.editorSave.emit({ uuid: this.uuid, value });
  }

  container: ElementRef<HTMLDivElement>;

  @ViewChild('editorContainer', { static: true })
  set editorContainer(container: ElementRef<HTMLDivElement>) {
    this.container = container;
    this.loadMonacoScripts().subscribe((monaco: typeof _monaco) => {
      monaco.editor.defineTheme('customTheme', {
        rules: [],
        colors: {},
        base: 'vs',
        inherit: true,
      });

      const languageRegistry = getRegistor(this.language() as keyof RegistorLanguage);
      this.loadLanguage(monaco, languageRegistry);

      this.editor = monaco.editor.create(container.nativeElement, {
        language: languageRegistry?.monacoId ?? this.language() ?? 'text',
        theme: 'customTheme',
        readOnly: this.readonly(),
      });

      this.editorService.setEditor(this.editor);

      this.editor.onDidChangeModelContent((e: _monaco.editor.IModelContentChangedEvent) => {
        this.editorService.setCurrentContent(this.editor.getValue());
        this.editorContentChange.emit({ content: this.editorService.getCurrentContent(), uuid: this.uuid });
      });

      const { registry, grammars } = this.registerGrammar(monaco.languages);
      wireTmGrammars(monaco, registry, grammars, this.editor).then();
      this.resetSize();
      this.editorReady.emit({ service: this.editorService, uuid: this.uuid });
    });
  }

  ngOnInit(): void {
    this.editorService.contentChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((content) => {
      this.editor?.setValue(content);
    });
  }

  ngOnDestroy(): void {
    this.editorDestroy.emit({ uuid: this.uuid });
  }

  private loadLanguage(monaco: typeof _monaco, languageRegistry: any): void {
    if (!languageRegistry) {
      return;
    }
    monaco.languages.register({ id: languageRegistry.monacoId, extensions: languageRegistry.extensions });
    monaco.languages.setMonarchTokensProvider(languageRegistry.monacoId, languageRegistry.monarchLanguage);
    monaco.languages.setLanguageConfiguration(languageRegistry.monacoId, languageRegistry.monarchLanguageConfiguration);
  }

  private resetSize(): void {
    const widthOptions = this.getWidthOptions();
    const heightOptions = this.getHeightOptions();

    const width = this.container.nativeElement.getBoundingClientRect().width - (widthOptions.padding ?? 0);
    const height = this.container.nativeElement.getBoundingClientRect().height - (heightOptions.padding ?? 0);

    this.editor?.layout({ height, width });
  }

  private loadMonacoScripts(): Observable<typeof _monaco> {
    const loader = new ReplaySubject<typeof _monaco>(1);

    if ((window as any).monacoEditorLoading) {
      interval(200)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          filter((_) => (window as any).monaco),
          take(1)
        )
        .subscribe((_) => {
          loader.next((window as any).monaco);
          loader.complete();
        });
      return loader;
    }

    (window as any).monacoEditorLoading = true;

    const script = document.createElement('script');
    script.src = '/assets/lib/monaco-editor/min/vs/loader.js';
    script.type = 'text/javascript';
    script.async = true;

    script.onload = () => {
      (window as any).require.config({ paths: { vs: '/assets/lib/monaco-editor/min/vs' } });
      (window as any).require(['vs/editor/editor.main'], () => {
        loader.next((window as any).monaco);
        loader.complete();
      });
    };

    document.body.appendChild(script);

    return loader;
  }

  private registerGrammar(languages: typeof _monaco.languages): { registry: Registry; grammars: Map<string, string> } {
    const registry = drools.getRegistry();
    const grammars = new Map();
    languages.register({ id: drools.monacoId });
    grammars.set(drools.monacoId, drools.tmScopeName);
    return { registry, grammars };
  }
}

/** Options used to customize the width of the editor. */
export type WidthOptions = {
  /**
   * Width of the editor.
   * @default 'auto'
   */
  value?: string;
  /**
   * size removed from the width via css calc.
   * @default 0
   */
  padding?: number;
};
const defaultValueWidthOptions: WidthOptions = { value: 'auto', padding: 0 };

/** Options used to customize the height of the editor. */
export type HeightOptions = {
  /**
   * height of the editor.
   * @default '100%'
   */
  value?: string;
  /**
   * size removed from the height via css calc.
   * @default 0
   */
  padding?: number;
};
const defaultValueHeightOptions: HeightOptions = { value: '360px', padding: 0 };

type EditorEvent = {
  uuid: string;
};

export type EditorReadyEvent = EditorEvent & {
  service: MonacoEditorService;
};

export type EditorDestroyEvent = EditorEvent & {};

export type EditorSaveEvent = EditorEvent & {
  value: string;
};

export type EditorContentChangeEvent = EditorEvent & {
  content: string;
};
